var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { COUNTRY_LIST } from "../../constants";
import { generateNativeUUID, toChecksumAddress, toSnakeCase, } from "../../utils";
import { getCurrentTimeFormatted } from "../../utils/timestamp";
import { isUndefined } from "../../validators";
import { logger } from "../logger";
import mergeDeepRight from "../ramda/mergeDeepRight";
import { local } from "../storage";
import { version } from "../version";
import { CHANNEL, VERSION } from "./constants";
import { generateAnonymousId } from "./utils";
var EventFactory = /** @class */ (function () {
    function EventFactory() {
        var _this = this;
        this.extractUTMParameters = function (url) {
            var result = {};
            try {
                var urlObj = new URL(url);
                var UTM_PREFIX_1 = "utm_";
                urlObj.searchParams.forEach(function (value, sParam) {
                    if (sParam.startsWith(UTM_PREFIX_1)) {
                        result[sParam] = value.trim() || "";
                    }
                });
            }
            catch (error) { }
            return result;
        };
        /**
         * Add any missing default page properties using values from options and defaults
         * @param properties Input page properties
         * @param options API options
         */
        this.getPageProperties = function (properties) {
            var pageProps = properties;
            if (isUndefined(pageProps.url)) {
                pageProps.url = new URL(globalThis.location.href).href;
            }
            if (isUndefined(pageProps.path)) {
                pageProps.path = globalThis.location.pathname;
            }
            if (isUndefined(pageProps.hash)) {
                pageProps.hash = globalThis.location.hash;
            }
            return pageProps;
        };
        this.getEnrichedEvent = function (formoEvent, context) {
            var commonEventData = {
                context: _this.generateContext(context),
                original_timestamp: getCurrentTimeFormatted(),
                user_id: formoEvent.user_id,
                type: formoEvent.type,
                channel: CHANNEL,
                version: VERSION,
            };
            if (!local.isAvailable()) {
                commonEventData.anonymous_id = generateNativeUUID();
            }
            else {
                commonEventData.anonymous_id = generateAnonymousId();
            }
            if (formoEvent.address) {
                commonEventData.address = toChecksumAddress(formoEvent.address);
            }
            else {
                commonEventData.address = formoEvent.address;
            }
            var processedEvent = mergeDeepRight(formoEvent, commonEventData);
            if (processedEvent.event === undefined) {
                processedEvent.event = null;
            }
            if (processedEvent.properties === undefined) {
                processedEvent.properties = null;
            }
            return toSnakeCase(processedEvent);
        };
    }
    EventFactory.prototype.getTimezone = function () {
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone;
        }
        catch (error) {
            logger.error("Error resolving timezone:", error);
            return "";
        }
    };
    EventFactory.prototype.getLocation = function () {
        try {
            var timezone = this.getTimezone();
            if (timezone in COUNTRY_LIST)
                return COUNTRY_LIST[timezone];
            return timezone;
        }
        catch (error) {
            logger.error("Error resolving location:", error);
            return "";
        }
    };
    EventFactory.prototype.getLanguage = function () {
        try {
            return ((navigator.languages && navigator.languages.length
                ? navigator.languages[0]
                : navigator.language) || "en");
        }
        catch (error) {
            logger.error("Error resolving language:", error);
            return "en";
        }
    };
    EventFactory.prototype.getLibraryVersion = function () {
        return version;
    };
    // Contextual fields that are automatically collected and populated by the Formo SDK
    EventFactory.prototype.generateContext = function (context) {
        var _a;
        var url = new URL(globalThis.location.href);
        var params = new URLSearchParams(url.search);
        var path = globalThis.location.pathname;
        var language = this.getLanguage();
        var timezone = this.getTimezone();
        var location = this.getLocation();
        var library_version = this.getLibraryVersion();
        // contextual properties
        var defaultContext = __assign(__assign({ user_agent: globalThis.navigator.userAgent, locale: language, timezone: timezone, location: location, referrer: document.referrer }, this.extractUTMParameters(globalThis.location.href)), { ref: ((_a = params.get("ref")) === null || _a === void 0 ? void 0 : _a.trim()) || "", page_path: path, page_title: document.title, page_url: url.href, library_name: "Formo Web SDK", library_version: library_version });
        var mergedContext = mergeDeepRight(defaultContext, context || {});
        return mergedContext;
    };
    EventFactory.prototype.generatePageEvent = function (category, name, properties, context) {
        var props = properties !== null && properties !== void 0 ? properties : {};
        props.category = category;
        props.name = name;
        props = this.getPageProperties(props);
        var pageEvent = {
            properties: props,
            type: "page",
        };
        return this.getEnrichedEvent(pageEvent, context);
    };
    EventFactory.prototype.generateDetectWalletEvent = function (providerName, rdns, properties, context) {
        var detectEvent = {
            properties: __assign({ providerName: providerName, rdns: rdns }, properties),
            type: "detect",
        };
        return this.getEnrichedEvent(detectEvent, context);
    };
    EventFactory.prototype.generateIdentifyEvent = function (providerName, rdns, address, userId, properties, context) {
        var identifyEvent = {
            properties: __assign({ providerName: providerName, rdns: rdns }, properties),
            user_id: userId,
            address: address,
            type: "identify",
        };
        return this.getEnrichedEvent(identifyEvent, context);
    };
    EventFactory.prototype.generateConnectEvent = function (chainId, address, properties, context) {
        var connectEvent = {
            properties: __assign({ chainId: chainId }, properties),
            address: address,
            type: "connect",
        };
        return this.getEnrichedEvent(connectEvent, context);
    };
    EventFactory.prototype.generateDisconnectEvent = function (chainId, address, properties, context) {
        var disconnectEvent = {
            properties: __assign({ chainId: chainId }, properties),
            address: address,
            type: "disconnect",
        };
        return this.getEnrichedEvent(disconnectEvent, context);
    };
    EventFactory.prototype.generateChainChangedEvent = function (chainId, address, properties, context) {
        var chainEvent = {
            properties: __assign({ chainId: chainId }, properties),
            address: address,
            type: "chain",
        };
        return this.getEnrichedEvent(chainEvent, context);
    };
    EventFactory.prototype.generateSignatureEvent = function (status, chainId, address, message, signatureHash, properties, context) {
        var signatureEvent = {
            properties: __assign({ status: status, chainId: chainId, message: message, signatureHash: signatureHash }, properties),
            address: address,
            type: "signature",
        };
        return this.getEnrichedEvent(signatureEvent, context);
    };
    EventFactory.prototype.generateTransactionEvent = function (status, chainId, address, data, to, value, transactionHash, properties, context) {
        var transactionEvent = {
            properties: __assign({ status: status, chainId: chainId, data: data, to: to, value: value, transactionHash: transactionHash }, properties),
            address: address,
            type: "transaction",
        };
        return this.getEnrichedEvent(transactionEvent, context);
    };
    EventFactory.prototype.generateTrackEvent = function (event, properties, context) {
        var transactionEvent = {
            properties: properties,
            event: event,
            type: "track",
        };
        return this.getEnrichedEvent(transactionEvent, context);
    };
    // Returns an event with type, context, properties, and common properties
    EventFactory.prototype.create = function (event, address, userId) {
        var formoEvent = {};
        formoEvent.address = address || null;
        formoEvent.user_id = userId || null;
        switch (event.type) {
            case "page":
                formoEvent = this.generatePageEvent(event.category, event.name, event.properties, event.context);
                break;
            case "detect":
                formoEvent = this.generateDetectWalletEvent(event.providerName, event.rdns, event.properties, event.context);
                break;
            case "identify":
                formoEvent = this.generateIdentifyEvent(event.providerName, event.rdns, event.address, event.userId, event.properties, event.context);
                break;
            case "chain":
                formoEvent = this.generateChainChangedEvent(event.chainId, event.address, event.properties, event.context);
                break;
            case "connect":
                formoEvent = this.generateConnectEvent(event.chainId, event.address, event.properties, event.context);
                break;
            case "disconnect":
                formoEvent = this.generateDisconnectEvent(event.chainId, event.address, event.properties, event.context);
                break;
            case "signature":
                formoEvent = this.generateSignatureEvent(event.status, event.chainId, event.address, event.message, event.signatureHash, event.properties, event.context);
                break;
            case "transaction":
                formoEvent = this.generateTransactionEvent(event.status, event.chainId, event.address, event.data, event.to, event.value, event.transactionHash, event.properties, event.context);
                break;
            case "track":
            default:
                formoEvent = this.generateTrackEvent(event.event, event.properties, event.context);
                break;
        }
        return formoEvent;
    };
    return EventFactory;
}());
export { EventFactory };
//# sourceMappingURL=EventFactory.js.map